/**
 * Arrow drawing tools.
 *
 * Two shapes, six tools:
 *   - `arrow` — two clicks: a shaft from A to B with a filled head at B.
 *   - `arrowMark{Up,Down,Left,Right}` — one click: a fixed-direction marker
 *     whose TIP sits on the clicked point, so it annotates the exact bar/price
 *     rather than floating beside it.
 *
 * All six share one template factory; only the heading differs.
 */
import { registerOverlay } from 'klinecharts'
import { isNum, lineFigure, type Coord, type OverlayFigure } from './overlayUtils'
import { DEFAULT_FIGURES, type OverlayTemplateParam, registerOnce } from './registrationUtils'

export interface ArrowStyle {
  color?: string
  size?: number
}

/** Length of the arrowhead barbs, px. */
const HEAD_LEN = 9
/** Half-angle between shaft and barb, radians (~26°). */
const HEAD_SPREAD = 0.45
/** Shaft length for the single-point directional marks, px. */
const MARK_LEN = 22

/** Heading in radians for each single-click mark, in canvas space (y grows down). */
export const ARROW_MARK_DIRECTIONS = {
  up: -Math.PI / 2,
  down: Math.PI / 2,
  left: Math.PI,
  right: 0,
} as const

export type ArrowMarkDirection = keyof typeof ARROW_MARK_DIRECTIONS

const finite = (p?: Coord) => !!p && isNum(p.x) && isNum(p.y)

/** Filled triangular head with its tip exactly on `tip`, pointing along `angle`. */
function headFigure(tip: Coord, angle: number, style: ArrowStyle): OverlayFigure {
  return {
    type: 'polygon',
    ignoreEvent: false,
    attrs: {
      coordinates: [
        tip,
        {
          x: tip.x - HEAD_LEN * Math.cos(angle - HEAD_SPREAD),
          y: tip.y - HEAD_LEN * Math.sin(angle - HEAD_SPREAD),
        },
        {
          x: tip.x - HEAD_LEN * Math.cos(angle + HEAD_SPREAD),
          y: tip.y - HEAD_LEN * Math.sin(angle + HEAD_SPREAD),
        },
      ],
    },
    // Solid head — an outlined arrowhead reads as a chevron at chart scale.
    styles: {
      style: 'fill',
      ...(style.color !== undefined ? { color: style.color } : {}),
    },
  }
}

/** Two-point arrow: shaft A→B, head at B. */
export function buildArrowFigures(coordinates: Coord[], style: ArrowStyle): OverlayFigure[] {
  if (coordinates.length < 2) return []
  const [from, to] = coordinates
  if (!finite(from) || !finite(to)) return []
  // A zero-length shaft has no heading — atan2(0,0) returns 0, which would
  // silently render a right-pointing arrow the user never drew.
  if (from.x === to.x && from.y === to.y) return []
  const angle = Math.atan2(to.y - from.y, to.x - from.x)
  return [
    lineFigure(from, to, { color: style.color, size: style.size }, true),
    headFigure(to, angle, style),
  ]
}

/** One-point directional mark: tip on the anchor, tail trailing behind it. */
export function buildArrowMarkFigures(
  coordinates: Coord[],
  direction: ArrowMarkDirection,
  style: ArrowStyle,
): OverlayFigure[] {
  const tip = coordinates[0]
  if (!finite(tip)) return []
  const angle = ARROW_MARK_DIRECTIONS[direction]
  const tail: Coord = {
    x: tip.x - MARK_LEN * Math.cos(angle),
    y: tip.y - MARK_LEN * Math.sin(angle),
  }
  return [
    lineFigure(tail, tip, { color: style.color, size: style.size }, true),
    headFigure(tip, angle, style),
  ]
}

/** Register the arrow overlay templates. Idempotent. */
export const registerArrowOverlays = registerOnce(() => {
  const readStroke = (overlay: unknown): ArrowStyle =>
    ((overlay as { styles?: { line?: ArrowStyle } })?.styles?.line ?? {}) as ArrowStyle

  registerOverlay({
    name: 'arrow',
    totalStep: 3, // two clicks
    ...DEFAULT_FIGURES,
    createPointFigures: ({ coordinates, overlay }) =>
      buildArrowFigures(coordinates as Coord[], readStroke(overlay)),
  } as OverlayTemplateParam)

  for (const dir of Object.keys(ARROW_MARK_DIRECTIONS) as ArrowMarkDirection[]) {
    registerOverlay({
      name: `arrowMark${dir[0].toUpperCase()}${dir.slice(1)}`,
      totalStep: 2, // single click
      ...DEFAULT_FIGURES,
      createPointFigures: ({ coordinates, overlay }) =>
        buildArrowMarkFigures(coordinates as Coord[], dir, readStroke(overlay)),
    } as OverlayTemplateParam)
  }
})
